import React from "react";

import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { faHouse, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CheckForm,
  Grid,
  Group,
  Input,
  Link,
  Section,
  Typography,
} from "@samskip/frontend-components";
import {
  Control,
  DeepRequired,
  FieldErrorsImpl,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { LabelConstants, DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { CommonTypes, DomesticTypes } from "@assets/types";
import { getErrorMessage } from "@assets/utilities/translateUtilities";

interface ReceiptDeliveryPlaceSectionProps {
  control: Control<DomesticFormTypes.CreateForm>;
  errors: FieldErrorsImpl<DeepRequired<DomesticFormTypes.CreateForm>>;
  onDeliveryModeChange: CommonTypes.CallbackFunction;
  onPhoneCopy: CommonTypes.CallbackFunction;
  register: UseFormRegister<DomesticFormTypes.CreateForm>;
  registeredFields: DomesticFormTypes.PlacesSectionFields;
  title?: string;
  type: DomesticConstants.RECEIPT_DELIVERY_PLACES;
}

const ReceiptDeliveryPlaceSection = ({
  control,
  errors,
  onDeliveryModeChange,
  onPhoneCopy,
  register,
  registeredFields,
  title,
  type,
}: ReceiptDeliveryPlaceSectionProps) => {
  const { t } = useTranslation();
  const deliveryMethod: DomesticConstants.DELIVERY_MODE = useWatch({
    control,
    name: registeredFields.Method,
  });

  const customerAddress: string = useWatch({
    control,
    name: registeredFields.CustomerAddressField,
  });

  const customerPostalCode: DomesticTypes.DomesticPostCode | null = useWatch({
    control,
    name: registeredFields.CustomerPostalCodeField,
  });

  const customerPhone: string = useWatch({
    control,
    name: registeredFields.CustomerPhoneField,
  });

  const isDoorAddress =
    deliveryMethod === DomesticConstants.DELIVERY_MODE.ADDRESS;

  return (
    <Section title={title}>
      <Group onChange={onDeliveryModeChange}>
        <CheckForm
          bordered
          id={`${type}-depot`}
          label={t(DomesticConstants.TRANSLATION_LABELS.DEPOT)}
          type="radio"
          value={DomesticConstants.DELIVERY_MODE.DEPOT}
          {...register(registeredFields.Method)}
        />
        <CheckForm
          bordered
          id={`${type}-address`}
          label={t(DomesticConstants.TRANSLATION_LABELS.ADDRESS)}
          type="radio"
          value={DomesticConstants.DELIVERY_MODE.ADDRESS}
          {...register(registeredFields.Method)}
        />
      </Group>
      <div className="flex flex-col gap-2">
        <Card id={`${type}-helper`}>
          <div className="flex flex-col gap-2">
            <p>
              {type === DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR
                ? isDoorAddress
                  ? t(
                      DomesticConstants.TRANSLATION_LABELS
                        .COLLECTION_SENDER_ADDRESS
                    )
                  : t(
                      DomesticConstants.TRANSLATION_LABELS
                        .COLLECTION_SENDER_DEPOT
                    )
                : isDoorAddress
                ? t(
                    DomesticConstants.TRANSLATION_LABELS
                      .DELIVERY_RECEIVER_ADDRESS
                  )
                : t(
                    DomesticConstants.TRANSLATION_LABELS.DELIVERY_RECEIVER_DEPOT
                  )}
            </p>
            <Typography
              color="neutral-900"
              fontWeight="semibold"
              icon={isDoorAddress ? faHouse : faLocationDot}
            >
              {customerAddress && customerPostalCode
                ? `${customerAddress}, ${customerPostalCode.PostCode} ${customerPostalCode.City}`
                : t(LabelConstants.TRANSLATION_LABELS.NO_ADDRESS_PROVIDED)}
            </Typography>
          </div>
        </Card>
        {!isDoorAddress && (
          <Typography variant="info">
            <Trans
              components={{
                hyperlink: (
                  <Link
                    className="inline"
                    href="https://www.samskip.is/innanlands/aaetlanir-og-afgreidslustadir/afgreidslustadir-og-umbodsmenn/"
                    target="_blank"
                  >
                    {" "}
                  </Link>
                ),
              }}
              i18nKey={DomesticConstants.TRANSLATION_LABELS.CLOSEST_WAREHOUSE}
            />
          </Typography>
        )}
      </div>

      {deliveryMethod === DomesticConstants.DELIVERY_MODE.ADDRESS && (
        <Group vertical>
          <p>
            {type === DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR
              ? t(
                  DomesticConstants.TRANSLATION_LABELS
                    .PROVIDE_COLLECTION_CONTACT_DETAILS
                )
              : t(
                  DomesticConstants.TRANSLATION_LABELS
                    .PROVIDE_DELIVERY_CONTACT_DETAILS
                )}
          </p>
          <Grid mdCol={3}>
            <Input
              className="col-span-2"
              errorMessage={getErrorMessage(
                registeredFields.ContactName,
                errors
              )}
              fullWidth
              hasError={errors[registeredFields.ContactName] != null}
              id={registeredFields.ContactName}
              label={t("LABEL_LOCATION_CONTACT_NAME")}
              maxLength={
                DomesticConstants.INPUT_CONSTRAINTS.MAX_CONTACT_NAME_LENGTH
              }
              placeholder={t("PLACEHOLDER_NAME")}
              required
              {...register(registeredFields.ContactName)}
            />

            <div className="flex flex-col gap-0.5">
              <Input
                errorMessage={getErrorMessage(
                  registeredFields.ContactPhone,
                  errors
                )}
                fullWidth
                hasError={errors[registeredFields.ContactPhone] != null}
                id={registeredFields.ContactPhone}
                label={t("LABEL_PHONE")}
                maxLength={
                  DomesticConstants.INPUT_CONSTRAINTS.MAX_PHONE_NUMBER_LENGTH
                }
                onlyDigits
                placeholder={t("PLACEHOLDER_PHONE")}
                required
                {...register(registeredFields.ContactPhone)}
              />
              <Link
                disabled={!customerPhone}
                icon={faCopy}
                onClick={onPhoneCopy}
              >
                {t(
                  type === DomesticConstants.RECEIPT_DELIVERY_PLACES.PLR
                    ? DomesticConstants.TRANSLATION_LABELS.COPY_FROM_SENDER
                    : DomesticConstants.TRANSLATION_LABELS.COPY_FROM_RECEIVER
                )}
              </Link>
            </div>
          </Grid>
        </Group>
      )}
    </Section>
  );
};

export default ReceiptDeliveryPlaceSection;
